// Polished Thread surface for the copilot chat panel.
//
// Layout note: the Composer is a SIBLING of the scroll viewport, not a child.
// In the previous structure the composer lived in ThreadPrimitive.ViewportFooter
// with `sticky bottom-0`, which only stays pinned when every ancestor honours
// the panel's max-height. When any ancestor leaked min-content height, the
// composer slid off-screen as the conversation grew. Moving it to a sibling
// layer makes the composer immune to viewport scroll state.
//
// The viewport reserves bottom-padding equal to the composer's measured height
// (CSS var --copilot-composer-h, set by Composer on autosize) plus a feather
// gradient that fades the last message under the composer rather than clipping
// it.

import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { ThreadPrimitive } from "@assistant-ui/react";
import { ArrowDownIcon } from "lucide-react";
import { UserMessage } from "./UserMessage";
import { AssistantMessage } from "./AssistantMessage";
import { SystemMessage } from "./SystemMessage";
import { Composer } from "./Composer";
import { ThreadWelcome } from "./ThreadWelcome";
import { ThinkingBubble } from "./ThinkingBubble";
import { PlanProgress } from "./PlanProgress";
import { PermissionCard } from "./PermissionCard";
import { GuideAdvanceChip } from "./GuideAdvanceChip";
import { useCopilotController } from "../CopilotRuntimeProvider";

// Distance-from-bottom (px) at which we still consider the user "sticky" —
// loose enough that subpixel rounding from the floating-composer height var
// doesn't flip us out of sticky-mode the way the library's 1px tolerance does.
const STICKY_THRESHOLD_PX = 80;

// Sticky-bottom enforcer.
//
// The library (assistant-ui useThreadViewportAutoScroll) already implements
// sticky auto-scroll, but it relies on (a) a 1px at-bottom tolerance and
// (b) MutationObserver / ResizeObserver on the viewport. Two cases slip past:
//
//   1. The placeholder pushed at the start of every turn contains a screenshot
//      data-URL <img>. Image loads don't fire mutation events on the parent
//      and don't change the viewport's box size, so the library never sees
//      the bubble grow ~600px after the image decodes.
//   2. With CSS `scroll-behavior: smooth`, the runStart scrollTo animates;
//      content pushed during the animation lands the smooth scroll at the
//      stale scrollHeight, leaving the user mid-thread.
//
// This component layers an explicit, observable-driven scroll-to-bottom on top
// of the library's behavior (additive, not replacing). MobX `observer` reads
// every message's text/actions/actionResults to subscribe to per-message
// updates; the effect (no deps) fires after every render and force-scrolls if
// the user was within STICKY_THRESHOLD_PX of bottom. A capture-phase `load`
// listener handles the image-decode case the library misses.
const StickyBottom = observer(function StickyBottom() {
  const controller = useCopilotController();
  const stickyRef = useRef(true);
  const viewportRef = useRef(null);

  useEffect(() => {
    const el = document.querySelector(".copilot-thread-viewport");
    if (!el) return undefined;
    viewportRef.current = el;

    // Track scrollTop direction so we don't flip out of sticky-mode just
    // because a programmatic scrollTo landed short of the bottom when
    // content grew during the scroll. The scroll event reports the CURRENT
    // scrollHeight, which may have grown after the browser clamped
    // scrollTop — so `dist` reads as > threshold even though we intended
    // to be pinned to the bottom. Same trick the assistant-ui library
    // uses ("ignore scroll-down events"): only re-evaluate stickiness on
    // user-initiated scroll-UP (scrollTop decreased), or re-stick when a
    // scroll-down lands inside the threshold.
    let lastScrollTop = el.scrollTop;
    const updateSticky = () => {
      const sT = el.scrollTop;
      const dist = el.scrollHeight - sT - el.clientHeight;
      if (sT < lastScrollTop) {
        // user scrolled up — re-evaluate freely
        stickyRef.current = dist < STICKY_THRESHOLD_PX;
      } else if (dist < STICKY_THRESHOLD_PX) {
        // scrolled down and landed near bottom — re-stick
        stickyRef.current = true;
      }
      // else: scroll-down landed short of bottom (programmatic mid-growth);
      // leave stickyRef as-is.
      lastScrollTop = sT;
    };
    updateSticky();
    el.addEventListener("scroll", updateSticky, { passive: true });

    // `load` doesn't bubble — capture phase catches it on the viewport.
    const onLoad = (e) => {
      if (!stickyRef.current) return;
      if (e.target?.tagName !== "IMG") return;
      el.scrollTop = el.scrollHeight;
    };
    el.addEventListener("load", onLoad, true);

    return () => {
      el.removeEventListener("scroll", updateSticky);
      el.removeEventListener("load", onLoad, true);
      viewportRef.current = null;
    };
  }, []);

  // Touch every observable that should trigger a re-render. Same pattern as
  // CopilotRuntimeProvider — reading inside `observer` subscribes us.
  void controller.busy;
  controller.messages.forEach((m) => {
    void m.text;
    void m.actions;
    void m.actionResults;
  });

  useEffect(() => {
    if (!stickyRef.current) return undefined;
    const el = viewportRef.current;
    if (!el) return undefined;
    const id = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  });

  return null;
});

export function Thread() {
  const controller = useCopilotController();
  return (
    <ThreadPrimitive.Root className="tw-relative tw-flex tw-flex-1 tw-min-h-0 tw-flex-col tw-bg-background tw-text-foreground">
      <ThreadPrimitive.Viewport className="copilot-thread-viewport tw-flex tw-flex-1 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-px-4 tw-pt-4">
        <ThreadPrimitive.Empty>
          <ThreadWelcome />
        </ThreadPrimitive.Empty>

        <ThreadPrimitive.Messages
          components={{
            UserMessage,
            AssistantMessage,
            SystemMessage,
          }}
        />

        <ThinkingBubble />
      </ThreadPrimitive.Viewport>

      <StickyBottom />

      {/* Feather gradient: fades the thread under the composer. */}
      <div aria-hidden="true" className="copilot-thread-feather" />

      {/* Floating composer layer — never scrolls with the conversation.
       *  Stacking order (top to bottom): scroll pip → permission card →
       *  guide-advance chip → plan progress strip → composer. The Guide-mode
       *  affordances live inside the floating layer so they sit above the
       *  composer (which is position:absolute) rather than behind it.
       *  PlanProgress publishes its height to --copilot-plan-h so the
       *  viewport's padding-bottom reserves room above the composer for it. */}
      <div className="copilot-composer-layer">
        <ThreadPrimitive.ScrollToBottom asChild>
          <button
            type="button"
            aria-label="Scroll to bottom"
            className="copilot-scroll-pip tw-rounded-full tw-border tw-border-border tw-bg-card tw-p-1.5 tw-text-foreground tw-shadow-md tw-transition hover:tw-bg-muted disabled:tw-invisible"
          >
            <ArrowDownIcon className="tw-h-4 tw-w-4" />
          </button>
        </ThreadPrimitive.ScrollToBottom>
        {/* Tier 1.c § 4.4 permission card — non-null while a control action
         *  is awaiting Allow/Deny. */}
        <PermissionCard controller={controller} />
        {/* Tier 1.c § 4.5 Guide-step advance chip — true after a Guide turn
         *  ends, gives the user [Done | Stop] to continue or end the walk-
         *  through. */}
        <GuideAdvanceChip controller={controller} />
        <PlanProgress />
        <Composer />
      </div>
    </ThreadPrimitive.Root>
  );
}
